import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { get5MatchService } from '../../services/get5MatchService';
import { matchService } from '../../services/matchService';
import ServerSelectionModal from './ServerSelectionModal';
import MapSelectionModal from './MapSelectionModal';
import { FaCircle, FaCopy } from 'react-icons/fa';
import { parseVetoLog } from '../../utils/vetoParser';
import './Get5MatchControl.scss';

// Map display names to CS:GO internal map names
const mapNameToInternal: Record<string, string> = {
  'Dust2': 'de_dust2',
  'Inferno': 'de_inferno',
  'Mirage': 'de_mirage',
  'Nuke': 'de_nuke',
  'Overpass': 'de_overpass',
  'Vertigo': 'de_vertigo',
  'Ancient': 'de_ancient',
  'Anubis': 'de_anubis',
  'Train': 'de_train',
  // Add any other maps as needed
};

const convertMapName = (displayName: string): string => {
  // Try exact match first
  if (mapNameToInternal[displayName]) {
    return mapNameToInternal[displayName];
  }
  
  // Try with first letter capitalized
  const capitalized = displayName.charAt(0).toUpperCase() + displayName.slice(1).toLowerCase();
  if (mapNameToInternal[capitalized]) {
    return mapNameToInternal[capitalized];
  }
  
  // Try all uppercase
  const upperKey = Object.keys(mapNameToInternal).find(
    key => key.toUpperCase() === displayName.toUpperCase()
  );
  if (upperKey) {
    return mapNameToInternal[upperKey];
  }
  
  // Fallback: assume it's already in the correct format or make it lowercase with de_ prefix
  if (displayName.startsWith('de_')) {
    return displayName.toLowerCase();
  }
  return 'de_' + displayName.toLowerCase();
};

interface Get5MatchControlProps {
  match: {
    challonge_id: string;
    team1: any;
    team2: any;
    veto?: string | {
      isComplete?: boolean;
      picks?: Array<{ map: string }>;
    };
  };
}

interface Get5Status {
  exists: boolean;
  get5MatchId?: number;
  status: 'not_created' | 'created' | 'live' | 'finished' | 'error';
  serverInfo?: {
    id: number;
    name: string;
    ip: string;
  };
}

const Get5MatchControl: React.FC<Get5MatchControlProps> = ({ match }) => {
  const { user } = useContext(AuthContext);
  const [status, setStatus] = useState<Get5Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showServerModal, setShowServerModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapStatus, setMapStatus] = useState<any>(null);
  const [selectedMap, setSelectedMap] = useState<{
    mapNumber: number;
    mapName: string;
    mapSide: string;
  } | null>(null);

  // Check if user is admin
  const isAdmin = user?.isAdmin || false;

  // Parse veto data - handle both string and object formats
  let vetoPicks: Array<{ map: string; side?: string; teamWithSideChoice?: string }> = [];
  let vetoComplete = false;
  
  if (match.veto) {
    if (typeof match.veto === 'string') {
      // Parse veto string to extract picked maps and side selections
      const vetoSteps = parseVetoLog(match.veto, match.team1, match.team2);
      
      // Get picked/decider maps
      const mapSelections = vetoSteps
        .filter(step => step.type === 'pick' || step.type === 'decider');
      
      // Create a map with side information
      vetoPicks = mapSelections.map(mapStep => {
        // Find the corresponding side selection for this map
        const sideStep = vetoSteps.find(
          step => step.type === 'side' && step.map === mapStep.map
        );
        
        return {
          map: mapStep.map,
          side: sideStep?.side,
          teamWithSideChoice: sideStep?.team?.teamName
        };
      });
      
      vetoComplete = vetoPicks.length > 0;
    } else if (match.veto.picks) {
      // Handle object format (legacy)
      vetoPicks = match.veto.picks;
      vetoComplete = vetoPicks.length > 0;
    }
  }

  // Determine if this is a BO3 match
  const isBo3 = vetoPicks.length > 1;

  useEffect(() => {
    loadStatus();
    if (isBo3) {
      loadMapStatus();
    }
  }, [match.challonge_id, isBo3]);

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await get5MatchService.checkGet5Status(match.challonge_id);
      setStatus(result);
    } catch (err) {
      console.error('Failed to load Get5 status:', err);
      setError('Failed to load match status');
    } finally {
      setLoading(false);
    }
  };

  const loadMapStatus = async () => {
    try {
      const result = await matchService.getMapStatus(match.challonge_id);
      setMapStatus(result);
    } catch (err) {
      console.error('Failed to load map status:', err);
    }
  };

  const handleCreateMatch = async (serverId: number, team1Get5Id: number, team2Get5Id: number) => {
    try {
      setCreating(true);
      setError(null);

      let mapNumber = 0;
      let mapName = '';
      let mapPool = '';
      let mapSides: string[] = [];

      if (isBo3 && selectedMap) {
        // BO3: Use selected map information
        mapNumber = selectedMap.mapNumber;
        mapName = convertMapName(selectedMap.mapName);
        mapPool = mapName;

        // Convert side format - find the corresponding veto pick to get team info
        const pickInfo = vetoPicks[selectedMap.mapNumber - 1]; // mapNumber is 1-indexed

        if (!pickInfo?.side || !pickInfo?.teamWithSideChoice) {
          mapSides = ['knife'];
        } else {
          const isTeam1Choice = pickInfo.teamWithSideChoice === match.team1.teamName;

          if (pickInfo.side === 'CT') {
            mapSides = [isTeam1Choice ? 'team1_ct' : 'team2_ct'];
          } else if (pickInfo.side === 'T') {
            mapSides = [isTeam1Choice ? 'team1_t' : 'team2_t'];
          } else {
            mapSides = ['knife'];
          }
        }
      } else {
        // BO1: Use all maps from veto
        mapPool = vetoPicks.map(p => convertMapName(p.map)).join(' ');

        // Create map sides array based on veto results
        mapSides = vetoPicks.map(pick => {
          if (!pick.side || !pick.teamWithSideChoice) {
            return 'knife';
          }

          const isTeam1Choice = pick.teamWithSideChoice === match.team1.teamName;

          if (pick.side === 'CT') {
            return isTeam1Choice ? 'team1_ct' : 'team2_ct';
          } else if (pick.side === 'T') {
            return isTeam1Choice ? 'team1_t' : 'team2_t';
          }

          return 'knife';
        });
      }

      const result = await get5MatchService.createGet5Match(match.challonge_id, {
        serverId,
        team1Get5Id,
        team2Get5Id,
        maxMaps: isBo3 ? 1 : vetoPicks.length,  // Force 1 for BO3
        mapPool,
        mapSides,
        mapNumber,   // 0 for BO1, 1/2/3 for BO3
        mapName: isBo3 ? mapName : undefined
      });

      if (result.success) {
        // Reload status
        await loadStatus();
        if (isBo3) {
          await loadMapStatus();
        }
        setShowServerModal(false);
        setSelectedMap(null);

        // Show success message
        const mapInfo = isBo3 ? `\nMap ${mapNumber}: ${selectedMap?.mapName}` : '';
        alert(`Get5 match created successfully!${mapInfo}\n\nMatch ID: ${result.get5MatchId}\nConnect: ${result.connectInfo?.ip}\nPassword: ${result.connectInfo?.password}`);
      }
    } catch (err: any) {
      console.error('Failed to create Get5 match:', err);
      setError(err.message || 'Failed to create match');
    } finally {
      setCreating(false);
    }
  };

  // Don't show anything if not admin
  if (!isAdmin) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div className="get5-match-control">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  // Error state
  if (error && !status) {
    return (
      <div className="get5-match-control">
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  // Veto not complete
  if (!vetoComplete) {
    return (
      <div className="get5-match-control">
        <div className="alert alert-info">
          Complete the veto process before creating a Get5 match
        </div>
      </div>
    );
  }

  // Handler for map selection
  const handleMapSelected = (mapNumber: number, mapName: string, mapSide: string) => {
    setSelectedMap({ mapNumber, mapName, mapSide });
    setShowMapModal(false);
    setShowServerModal(true);
  };

  // Handler for create button click
  const handleCreateClick = () => {
    if (isBo3) {
      setShowMapModal(true);  // Show map selection first
    } else {
      setShowServerModal(true);  // Go directly to server selection
    }
  };

  // BO3: Show button if not all maps created yet
  const shouldShowButton = !isBo3 || (mapStatus && mapStatus.totalMaps < vetoPicks.length);

  // Get5 match exists - show status info, and for BO3 also show create button if more maps needed
  if (status?.exists) {
    const statusClass = status.status === 'live' ? 'status-live' :
                       status.status === 'finished' ? 'status-finished' :
                       status.status === 'error' ? 'status-error' : 'status-created';

    const handleCopyServerIp = () => {
      if (status.serverInfo?.ip) {
        navigator.clipboard.writeText(status.serverInfo.ip);
        alert('Server IP copied to clipboard!');
      }
    };

    return (
      <div className="get5-match-control">
        <div className="status-info">
          <div className={`status-chip ${statusClass} ${status.status === 'live' ? 'pulse' : ''}`}>
            <FaCircle className="status-icon" />
            <span>Get5 Match: {status.get5MatchId}</span>
          </div>
          {status.serverInfo && (
            <span className="server-info">on {status.serverInfo.name}</span>
          )}
        </div>
        {status.serverInfo?.ip && (
          <div className="server-connect-info">
            <label htmlFor="get5-server-ip">Server IP:</label>
            <div className="ip-field">
              <input
                id="get5-server-ip"
                type="text"
                value={status.serverInfo.ip}
                readOnly
                className="server-ip-input"
              />
              <button
                className="copy-button"
                onClick={handleCopyServerIp}
                title="Copy to clipboard"
              >
                <FaCopy />
              </button>
            </div>
          </div>
        )}

        {/* BO3: Show create button for next map if series not complete */}
        {isBo3 && shouldShowButton && (
          <div className="create-section" style={{ marginTop: '1rem' }}>
            <div className="alert alert-info">
              Maps created: {mapStatus?.completedMaps + mapStatus?.liveMaps + mapStatus?.pendingMaps || 0} / {vetoPicks.length}
              {mapStatus?.maps && mapStatus.maps.length > 0 && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.9em' }}>
                  {mapStatus.maps.map((m: any) => (
                    <div key={m.map_number}>
                      Map {m.map_number}: {m.map_name} - {m.status}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              className="button button-primary"
              onClick={handleCreateClick}
              disabled={creating}
            >
              {creating ? 'Creating...' : `Create Map ${(mapStatus?.totalMaps || 0) + 1}`}
            </button>
          </div>
        )}

        {showMapModal && (
          <MapSelectionModal
            matchId={match.challonge_id}
            maxMaps={vetoPicks.length}
            vetoPicks={vetoPicks}
            onClose={() => setShowMapModal(false)}
            onConfirm={handleMapSelected}
          />
        )}

        {showServerModal && (
          <ServerSelectionModal
            match={match}
            vetoPicks={vetoPicks}
            onClose={() => {
              setShowServerModal(false);
              setSelectedMap(null);
            }}
            onConfirm={handleCreateMatch}
          />
        )}
      </div>
    );
  }

  // No Get5 match yet (or BO3 with more maps to create)
  return (
    <div className="get5-match-control">
      <div className="create-section">
        <div className="alert alert-info">
          {isBo3 && mapStatus ? (
            <>
              Maps created: {mapStatus.completedMaps + mapStatus.liveMaps + mapStatus.pendingMaps} / {vetoPicks.length}
              {mapStatus.maps.length > 0 && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.9em' }}>
                  {mapStatus.maps.map((m: any) => (
                    <div key={m.map_number}>
                      Map {m.map_number}: {m.map_name} - {m.status}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>No Get5 match created yet. Maps selected: {vetoPicks.map(p => p.map).join(', ')}</>
          )}
        </div>
        {shouldShowButton && (
          <button
            className="button button-primary"
            onClick={handleCreateClick}
            disabled={creating}
          >
            {creating ? 'Creating...' : isBo3 ? `Create Get5 Match (${mapStatus?.totalMaps || 0}/${vetoPicks.length} created)` : 'Create Get5 Match'}
          </button>
        )}
      </div>

      {showMapModal && (
        <MapSelectionModal
          matchId={match.challonge_id}
          maxMaps={vetoPicks.length}
          vetoPicks={vetoPicks}
          onClose={() => setShowMapModal(false)}
          onConfirm={handleMapSelected}
        />
      )}

      {showServerModal && (
        <ServerSelectionModal
          match={match}
          vetoPicks={vetoPicks}
          onClose={() => {
            setShowServerModal(false);
            setSelectedMap(null);
          }}
          onConfirm={handleCreateMatch}
        />
      )}
    </div>
  );
};

export default Get5MatchControl;